import { isCardAllowedForDashboardType } from '@/constants/ReportDashboard/card-exclusivity'
import { RADashboardCardId } from '@/constants/ReportDashboard/cards-enums'
import { FADashboardType } from '@/constants/ReportDashboard/dashboards/dashboard-types'
import { ReportItem, ReportTotalItem } from '@/constants/ReportDashboard/report-items'
import { CardProps } from '@/interfaces/raDashboard.interface'
import React from 'react'
import EmptyCard from '../EmptyCard'
import FactorBreakdownCard from '../FactorLens/FactorBreakdownCard'
import FactorGridCard from '../FactorLens/FactorGridCard'
import FactorRiskCard from '../FactorLens/FactorRiskCard'
import FactorWaterfallCard from '../FactorLens/FactorWaterfallCard'
import HoldingsTableCard from '../Holdings/HoldingsTableCard'
import TopBottomContributionCard from '../Holdings/TopBottomContributionCard'
import AttributionBubbleCard from '../Holdings/AttributionBubbleCard'
import PortfolioHoldings from '../Holdings/PortfolioHoldings'
import RiskVisualCard from '../Holdings/RiskVisualCard'
import TopBottomTableCard from '../Holdings/TopBottomTableCard'
import AttributionReturnCard from '../Overview/AttributionReturnCard'
import DailyReturnChart from '../Overview/DailyReturnChart'
import DataPointCard from '../Overview/DataPointCard'
import FactorAttributionChart from '../Overview/FactorAttributionChart'
import PortfolioReturnChartCard from '../Overview/PortfolioReturnChartCard'
import BenchmarkReturnChartCard from '../Overview/BenchmarkReturnChartCard'
import ActiveReturnChartCard from '../Overview/ActiveReturnChartCard'
import RiskMetricsSummaryCard from '../Overview/RiskMetricsSummaryCard'
import TrailingRiskCard from '../Overview/TrailingRiskCard'

const dataPointCardMap: Partial<Record<RADashboardCardId, ReportTotalItem>> = {
  [RADashboardCardId.Point_DV01]: ReportItem.DV01,
  [RADashboardCardId.Point_Convexity]: ReportItem.Convexity,
  [RADashboardCardId.Point_Duration]: ReportItem.EffectiveDuration,
  [RADashboardCardId.Point_SpreadDuration]: ReportItem.SpreadDuration,
  [RADashboardCardId.Point_YieldToMaturity]: ReportItem.YieldToMaturity,
  [RADashboardCardId.Point_YieldToWorst]: ReportItem.YieldToWorst,
  [RADashboardCardId.Point_YearsToMaturity]: ReportItem.YearsToMaturity,
  [RADashboardCardId.Point_CouponRate]: ReportItem.CouponRate,
  [RADashboardCardId.Point_DividendYield]: ReportItem.DividendYield,
  [RADashboardCardId.Point_AverageLife]: ReportItem.AverageLife,
  [RADashboardCardId.Point_Beta]: ReportItem.Beta,
  [RADashboardCardId.Point_BookToPrice]: ReportItem.BookToPrice,
  [RADashboardCardId.Point_BookValue]: ReportItem.BookValue,
  [RADashboardCardId.Point_CashFlowYield]: ReportItem.CashFlowYield,
  [RADashboardCardId.Point_CurrentYield]: ReportItem.CurrentYield,
  [RADashboardCardId.Point_EPRatio]: ReportItem.EPRatio,
  [RADashboardCardId.Point_EarningsPerShare]: ReportItem.EarningsPerShare,
  [RADashboardCardId.Point_EffectiveD2]: ReportItem.EffectiveD2,
  [RADashboardCardId.Point_EffectiveD3]: ReportItem.EffectiveD3,
  [RADashboardCardId.Point_DurContributionPct]: ReportItem.DurContributionPct,
  [RADashboardCardId.Point_DurContributionYrs]: ReportItem.DurContributionYrs,
  [RADashboardCardId.Point_EffectiveSpreadDuration]: ReportItem.EffectiveSpreadDuration,
  [RADashboardCardId.Point_SpreadDurContributionPct]: ReportItem.SpreadDurContributionPct,
  [RADashboardCardId.Point_SpreadDurContributionYrs]: ReportItem.SpreadDurContributionYrs,
  [RADashboardCardId.Point_GainLoss]: ReportItem.GainLoss,
  [RADashboardCardId.Point_MacaulayDuration]: ReportItem.MacaulayDuration,
  [RADashboardCardId.Point_MarketCap]: ReportItem.MarketCap,
  [RADashboardCardId.Point_MarketCapLog]: ReportItem.MarketCapLog,
  [RADashboardCardId.Point_MarketValue]: ReportItem.MarketValue,
  [RADashboardCardId.Point_MarketValuePar]: ReportItem.MarketValuePar,
  [RADashboardCardId.Point_ModifiedDuration]: ReportItem.ModifiedDuration,
  [RADashboardCardId.Point_Momentum]: ReportItem.Momentum,
  [RADashboardCardId.Point_MoodyQuality]: ReportItem.MoodyQuality,
  [RADashboardCardId.Point_NotionalValue]: ReportItem.NotionalValue,
  [RADashboardCardId.Point_OAS]: ReportItem.OAS,
  [RADashboardCardId.Point_PBRatio]: ReportItem.PBRatio,
  [RADashboardCardId.Point_PERatio]: ReportItem.PERatio,
  [RADashboardCardId.Point_PctFaceMarketValue]: ReportItem.PctFaceMarketValue,
  [RADashboardCardId.Point_PctMarketValue]: ReportItem.PctMarketValue,
  [RADashboardCardId.Point_Price]: ReportItem.Price,
  [RADashboardCardId.Point_SPQuality]: ReportItem.SPQuality,
  [RADashboardCardId.Point_SecurityCount]: ReportItem.SecurityCount,
  [RADashboardCardId.Point_TotalAccruedInterest]: ReportItem.BaseTotalAccruedInterest,
  [RADashboardCardId.Point_TrailingEPS]: ReportItem.TrailingEPS,
  [RADashboardCardId.Point_Volatility]: ReportItem.Volatility,
  [RADashboardCardId.Point_YearsToWorst]: ReportItem.YearsToWorst,
  // End DataPointCardMap
}

const ATTRIBUTION_CARD_IDS = new Set<RADashboardCardId>([
  RADashboardCardId.Return_Portfolio,
  RADashboardCardId.Return_Benchmark,
  RADashboardCardId.Return_Active,
  RADashboardCardId.Chart_DailyReturn,
  RADashboardCardId.Chart_PortfolioReturn,
  RADashboardCardId.Chart_BenchmarkReturn,
  RADashboardCardId.Chart_ActiveReturn,
  RADashboardCardId.Chart_FactorAttribution,
  RADashboardCardId.Table_Holdings,
  RADashboardCardId.Table_HoldingsTopBottom,
  RADashboardCardId.Chart_HoldingsBubble,
])

// Defined outside any parent grid component so React sees a stable component type across
// renders. If defined inline, every re-render produces a new function reference, causing
// React to unmount and remount all cards (resetting state, ag-Grid instances, and refs).
export function CardRenderer({
  cardProps,
  dashboardType,
}: {
  cardProps: CardProps
  dashboardType: FADashboardType
}): React.ReactElement {
  const key = cardProps.key as RADashboardCardId
  // Guard: attribution cards only render on a dashboard type they're allowed on.
  if (ATTRIBUTION_CARD_IDS.has(key) && !isCardAllowedForDashboardType(key, dashboardType)) {
    return <EmptyCard {...cardProps} />
  }
  const dataPoint = dataPointCardMap[cardProps.key as RADashboardCardId]
  if (dataPoint !== undefined) {
    return <DataPointCard {...cardProps} dataPoint={dataPoint} />
  }
  switch (cardProps.key) {
    case RADashboardCardId.MetricsSummaryRisk:
      return <RiskMetricsSummaryCard {...cardProps} />
    case RADashboardCardId.FactorLensRisk:
      return <FactorRiskCard {...cardProps} />
    case RADashboardCardId.FactorWaterfallRisk:
      return <FactorWaterfallCard {...cardProps} />
    case RADashboardCardId.FactorLensGridRisk:
      return <FactorGridCard {...cardProps} />
    case RADashboardCardId.FactorBreakdownRisk:
      return <FactorBreakdownCard {...cardProps} />
    case RADashboardCardId.Point_TrailingRisk:
      return <TrailingRiskCard {...cardProps} />
    case RADashboardCardId.HoldingsTopBottom:
      return <TopBottomTableCard {...cardProps} />
    case RADashboardCardId.HoldingsRiskVisualization:
      return <RiskVisualCard {...cardProps} />
    case RADashboardCardId.HoldingsContributionRisk:
      return <PortfolioHoldings {...cardProps} />
    // Attribution cards read their own data from FAAttributionDataContext via hooks.
    case RADashboardCardId.Return_Portfolio:
      return <AttributionReturnCard {...cardProps} metric='Portfolio' />
    case RADashboardCardId.Return_Benchmark:
      return <AttributionReturnCard {...cardProps} metric='Benchmark' />
    case RADashboardCardId.Return_Active:
      return <AttributionReturnCard {...cardProps} metric='Active' />
    case RADashboardCardId.Chart_DailyReturn:
      return <DailyReturnChart {...cardProps} />
    case RADashboardCardId.Chart_PortfolioReturn:
      return <PortfolioReturnChartCard {...cardProps} />
    case RADashboardCardId.Chart_BenchmarkReturn:
      return <BenchmarkReturnChartCard {...cardProps} />
    case RADashboardCardId.Chart_ActiveReturn:
      return <ActiveReturnChartCard {...cardProps} />
    case RADashboardCardId.Chart_FactorAttribution:
      return <FactorAttributionChart {...cardProps} />
    case RADashboardCardId.Table_Holdings:
      return <HoldingsTableCard {...cardProps} />
    case RADashboardCardId.Table_HoldingsTopBottom:
      return <TopBottomContributionCard {...cardProps} />
    case RADashboardCardId.Chart_HoldingsBubble:
      return <AttributionBubbleCard {...cardProps} />
    default:
      return <EmptyCard {...cardProps} />
  }
}
